import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  standardColours,
  brandColours,
  brandFonts,
  fontSize,
} from '../styles';
import { Container, Heading, HtmlContent, Button } from './ui';

const StyledImageContent = styled.section`
  ${({ isTimelineEvent }) =>
    isTimelineEvent ? sectionMargins(undefined, '60px') : sectionMargins()};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: ${({ flip, isTimelineEvent }) =>
      isTimelineEvent ? 'repeat(2, 1fr)' : flip ? '1fr 1.15fr' : '1.15fr 1fr'};
    align-items: center;
    gap: 40px;
  `}

  ${minBreakpointQuery.medium`
    gap: 60px;
  `}

  ${minBreakpointQuery.large`
    gap: 80px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 100px;
  `}

  ${({ isTimelineEvent }) => {
    if (!isTimelineEvent) {
      return css`
        ${minBreakpointQuery.xxlarge`
          gap: 120px;
        `}
      `;
    }
  }}
`;

const StyledImage = styled(GatsbyImage)`
  ${maxBreakpointQuery.smedium`
    aspect-ratio: 2/1;

    @supports not (aspect-ratio: 3/2) {
      max-height: 430px;
    }
  `}

  ${maxBreakpointQuery.small`
    aspect-ratio: 3/2;
  `}

  ${maxBreakpointQuery.tsmall`
    aspect-ratio: 4/3;
  `}

  ${minBreakpointQuery.smedium`
    height: 100%;
  `}

  ${({ flip }) => {
    if (flip) {
      return css`
        ${minBreakpointQuery.smedium`
          grid-row: 1;
          grid-column: 2;
        `}
      `;
    }
  }}

  ${({ version }) => {
    if (version === 2) {
      return css`
        ${({ flip }) => (flip ? 'margin-right' : 'margin-left')}: -30px;

        ${maxBreakpointQuery.smedium`
          ${({ flip }) => (flip ? 'margin-left' : 'margin-right')}: -30px;
        `}
      `;
    }
  }}
`;

const StyledContent = styled.div`
  ${minBreakpointQuery.smedium`
    margin-top: 30px;
    margin-bottom: 30px;
  `}

  ${({ version }) => {
    if (version === 2) {
      return css`
        ${minBreakpointQuery.smedium`
          ${({ flip }) => (flip ? 'margin-left' : 'margin-right')}: auto;
          max-width: 620px;
        `}
      `;
    }
  }}
`;

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
`;

const StyledYear = styled.span`
  display: inline-block;
  margin-right: 20px;
  padding: 8px 20px;
  color: ${standardColours.white};
  font-family: ${brandFonts.secondary};
  ${fontSize(32)};
  background-color: ${brandColours.tertiary};

  ${minBreakpointQuery.small`
    margin-right: 25px;
    padding-right: 25px;
    padding-left: 25px;
    ${fontSize(40)};
  `}

  ${minBreakpointQuery.large`
    margin-right: 30px;
    padding-right: 30px;
    padding-left: 30px;
    ${fontSize(48)};
  `}
`;

const StyledText = styled(HtmlContent)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const StyledLink = styled(Button)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const ImageContent = ({
  image,
  year,
  heading,
  text,
  link,
  flip,
  version,
  isTimelineEvent,
}) => (
  <StyledImageContent isTimelineEvent={isTimelineEvent}>
    <Container wide={version === 2}>
      <StyledInner flip={flip} isTimelineEvent={isTimelineEvent}>
        <StyledImage
          image={image.gatsbyImageData}
          alt={image.alt}
          flip={flip}
          version={version}
        />
        <StyledContent flip={flip} version={version}>
          <StyledHeader>
            {isTimelineEvent && <StyledYear>{year}</StyledYear>}
            <Heading>{heading}</Heading>
          </StyledHeader>
          {text && <StyledText html={text} />}
          {link && <StyledLink to={link.page}>{link.text}</StyledLink>}
        </StyledContent>
      </StyledInner>
    </Container>
  </StyledImageContent>
);

export default ImageContent;
